import { isPubliclyRoutable } from './publicRoutability.js';
import { imgUrl } from './imgUrl.js';
// ─── src/utils/structuredData.js ─────────────────────────────────────────────
// Pure JSON-LD / Schema.org builder functions.
// NO React imports. These return plain objects - pass them to <JsonLd schema={...} />.
// Accepts both snake_case (DB rows) and camelCase (mapped listing objects).

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://luxuryweddingdirectory.com";

const SITE_NAME = "Luxury Wedding Directory";
const SITE_LOGO = `${SITE_URL}/logo.png`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function defined(...values) {
  for (const v of values) {
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return undefined;
}

// ISO 3166-1 alpha-2 codes for countries in the directory.
// Slug → ISO2 mapping used for schema.org addressCountry.
const COUNTRY_ISO2 = {
  "england":          "GB",
  "scotland":         "GB",
  "wales":            "GB",
  "ireland":          "IE",
  "northern-ireland": "GB",
  "france":           "FR",
  "italy":            "IT",
  "spain":            "ES",
  "portugal":         "PT",
  "greece":           "GR",
  "germany":          "DE",
  "austria":          "AT",
  "switzerland":      "CH",
  "croatia":          "HR",
  "malta":            "MT",
  "cyprus":           "CY",
  "netherlands":      "NL",
  "belgium":          "BE",
  "denmark":          "DK",
  "sweden":           "SE",
  "norway":           "NO",
  "usa":              "US",
  "united-states":    "US",
  "mexico":           "MX",
  "brazil":           "BR",
  "canada":           "CA",
  "australia":        "AU",
  "new-zealand":      "NZ",
  "south-africa":     "ZA",
  "kenya":            "KE",
  "mauritius":        "MU",
  "maldives":         "MV",
  "bali":             "ID",
  "indonesia":        "ID",
  "thailand":         "TH",
  "cambodia":         "KH",
  "vietnam":          "VN",
  "japan":            "JP",
  "india":            "IN",
  "sri-lanka":        "LK",
  "uae":              "AE",
  "united-arab-emirates": "AE",
  "turkey":           "TR",
  "morocco":          "MA",
  "egypt":            "EG",
};

/**
 * Returns the ISO 3166-1 alpha-2 code for a country slug/name.
 * Falls back to title-casing the raw value for unlisted countries.
 */
function normaliseCountry(raw) {
  if (!raw) return undefined;
  const slug = raw.toLowerCase().replace(/\s+/g, "-");
  if (COUNTRY_ISO2[slug]) return COUNTRY_ISO2[slug];
  // Title-case fallback: "cambodia" → "Cambodia", "new zealand" → "New Zealand"
  return raw.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// Three-letter codes and local abbreviations that appear in price_currency,
// mapped to the symbol a reader expects. Anything not listed is used verbatim,
// which is right for a symbol we already hold (€, $, ฿) and harmless for a code.
const CURRENCY_SYMBOL = {
  GBP: "£", EUR: "€", USD: "$", AUD: "$", CAD: "$", NZD: "$",
  CHF: "CHF ", AED: "AED ", CZK: "Kč ", THB: "฿", ZAR: "R",
  MYR: "RM ", INR: "₹", JPY: "¥", MXN: "$", SGD: "$",
  Kc: "Kč ", R: "R", RM: "RM ", "Kč": "Kč ",
};

/**
 * Derives a schema.org priceRange string from a priceFrom value, in the venue's
 * OWN currency.
 *
 * This used to hardcode a pound sign on every bare number. 93 of the 181 priced
 * listings, more than half, are not priced in pounds, so their live pages told
 * Google the wrong currency and, for a euro venue, roughly 17% more money than it
 * charges. Villa La Rondinaia charges €15,000 a night and published
 * "From £15,000". The listings table has carried price_currency all along.
 *
 * "From £5,500" → passed through, already formatted
 * 15000 + "€"   → "From €15,000"
 * 15000 + none  → "From £15,000", the historic behaviour, for a UK-priced venue
 */
function derivePriceRange(raw, currency) {
  if (!raw) return undefined;
  const s = String(raw).trim();
  // Already a formatted string (contains currency symbol or "From")
  if (/[£$€¥₹]|From|from/i.test(s)) return s;
  // Numeric, format with comma separator
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (isNaN(n)) return s;
  const cur = String(currency || "").trim();
  const sym = cur ? (CURRENCY_SYMBOL[cur] ?? (cur.length > 2 ? `${cur} ` : cur)) : "£";
  return `From ${sym}${n.toLocaleString("en-GB")}`;
}

// ── buildVenueSchema ──────────────────────────────────────────────────────────
// LocalBusiness schema for venue listing pages.
// Accepts both snake_case DB rows and camelCase mapped objects.
/**
 * @param {object} listing - Row from `listings` table (snake_case or camelCase)
 */
export function buildVenueSchema(listing) {
  if (!listing) return null;

  const name   = defined(listing.name, listing.venue_name, listing.venueName);
  const slug   = defined(listing.slug);

  // Build canonical URL using the listing's actual geo + category path.
  // Vendors live at /{country}/{region}/{category}/{slug} while traditional
  // venue-only paths use /wedding-venues/{slug}.  Fall back progressively.
  const countrySlug  = defined(listing.countrySlug,  listing.country_slug);
  const regionSlug   = defined(listing.regionSlug,   listing.region_slug);
  const categorySlug = defined(listing.categorySlug, listing.category_slug);
  // Default category for URL construction so this schema url is identical to the
  // page <link rel="canonical"> (VenueProfile defaults the category the same way).
  const categoryForUrl = categorySlug || "wedding-venues";

  const url = (() => {
    if (!slug) return SITE_URL;
    if (countrySlug && regionSlug) {
      return `${SITE_URL}/${countrySlug}/${regionSlug}/${categoryForUrl}/${slug}`;
    }
    if (countrySlug) {
      return `${SITE_URL}/${countrySlug}/${categoryForUrl}/${slug}`;
    }
    if (categorySlug && categorySlug !== "wedding-venues") {
      return `${SITE_URL}/${categorySlug}/${slug}`;
    }
    return `${SITE_URL}/wedding-venues/${slug}`;
  })();

  // Hero image - try both naming conventions
  const image  = defined(
    listing.hero_image,
    listing.heroImage,
    listing.hero_image_set?.[0],
    listing.heroImageSet?.[0],
    listing.imgs?.[0]?.src || listing.imgs?.[0],
    listing.gallery?.[0]?.src || listing.gallery?.[0],
  );

  const desc   = defined(
    listing.seo_description,
    listing.seoDescription,
    listing.short_description,
    listing.shortDescription,
    listing.description,
  );

  const schema = {
    "@context":   "https://schema.org",
    "@type":      categorySlug === "wedding-planners"
      ? ["LocalBusiness", "ProfessionalService"]
      : "LocalBusiness",
    "name":       name,
    "url":        url,
  };

  if (desc)  schema.description = desc;
  if (image) schema.image       = imgUrl(image, { width: 1200 });

  const priceRange = defined(listing.priceRange, listing.price_range)
    || derivePriceRange(
         defined(listing.priceFrom, listing.price_from, listing.price_from_numeric),
         defined(listing.priceCurrency, listing.price_currency),
       );
  if (priceRange) schema.priceRange = priceRange;

  // Address
  const streetAddress  = defined(listing.address, listing.address_line1, listing.addressLine1);
  const city           = defined(listing.city, listing.location_city, listing.locationCity);
  const region         = defined(listing.region, listing.county);
  const countryRaw     = defined(listing.country);
  const addressCountry = normaliseCountry(countryRaw);
  const postcode       = defined(listing.postcode, listing.postal_code, listing.postalCode);

  if (city || addressCountry) {
    schema.address = {
      "@type": "PostalAddress",
      ...(streetAddress  && { streetAddress }),
      ...(city           && { addressLocality: city }),
      ...(region         && { addressRegion: region }),
      ...(addressCountry && { addressCountry }),
      ...(postcode       && { postalCode: postcode }),
    };
  }

  // Aggregate rating
  const avgRating   = defined(listing.avg_rating,   listing.avgRating,   listing.rating);
  const reviewCount = defined(listing.review_count, listing.reviewCount, listing.reviews);

  if (avgRating && reviewCount > 0) {
    schema.aggregateRating = {
      "@type":       "AggregateRating",
      "ratingValue": parseFloat(avgRating).toFixed(1),
      "reviewCount": reviewCount,
      "bestRating":  "5",
      "worstRating": "1",
    };
  }

  // Telephone / email
  const phone = defined(listing.phone, listing.contact_phone, listing.contactPhone);
  const email = defined(listing.email, listing.contact_email, listing.contactEmail);
  if (phone) schema.telephone = phone;
  if (email) schema.email     = email;

  // GeoCoordinates, lat/lng stored in listings table, unlocks local pack + map citations
  const lat = defined(listing.lat, listing.latitude);
  const lng = defined(listing.lng, listing.longitude);
  if (lat != null && lng != null) {
    schema.geo = {
      "@type":     "GeoCoordinates",
      "latitude":  lat,
      "longitude": lng,
    };
  }

  // Planner-specific: areaServed from planner_destinations (comma-separated string)
  if (categorySlug === "wedding-planners") {
    const destinations = defined(listing.planner_destinations, listing.plannerDestinations);
    if (destinations && typeof destinations === "string" && destinations.trim()) {
      schema.areaServed = destinations
        .split(",")
        .map(d => d.trim())
        .filter(Boolean)
        .map(d => ({ "@type": "Place", "name": d }));
    } else if (Array.isArray(destinations) && destinations.length) {
      schema.areaServed = destinations.map(d => ({ "@type": "Place", "name": d }));
    }
  }

  return schema;
}

// ── buildArticleSchema ────────────────────────────────────────────────────────
// Article schema for magazine post pages.
/**
 * @param {object} post - Row from `magazine_posts` table
 */
export function buildArticleSchema(post) {
  if (!post) return null;

  // Editorial/magazine articles are canonically served at /lwd-edit/{slug} — one segment,
  // no category. Keep this identical to the page <link rel="canonical"> and the sitemap.
  const url   = post.slug
    ? `${SITE_URL}/lwd-edit/${post.slug}`
    : SITE_URL;

  const schema = {
    "@context":         "https://schema.org",
    "@type":            "Article",
    "headline":         defined(post.seo_title, post.seoTitle, post.title),
    "description":      defined(post.seo_description, post.seoDescription, post.excerpt),
    "url":              url,
    "publisher": {
      "@type": "Organization",
      "name":  SITE_NAME,
      "logo":  { "@type": "ImageObject", "url": SITE_LOGO },
    },
  };

  if (post.hero_image || post.heroImage) {
    schema.image = imgUrl(defined(post.hero_image, post.heroImage), { width: 1200 });
  }
  if (post.published_at || post.publishedAt) {
    schema.datePublished = defined(post.published_at, post.publishedAt);
  }
  if (post.updated_at || post.updatedAt) {
    schema.dateModified = defined(post.updated_at, post.updatedAt);
  }

  // Author, prefer linked DB author with @id for E-E-A-T disambiguation
  const authorObj  = post.author && typeof post.author === 'object' ? post.author : null;
  const authorName = defined(
    authorObj?.name,
    post.author_name, post.authorName,
    typeof post.author === 'string' ? post.author : undefined,
  );
  if (authorName) {
    schema.author = { "@type": "Person", "name": authorName };
    // Inject @id when the author has a slug (linked via AuthorManagementModule)
    const authorSlug = defined(authorObj?.slug, post.author_slug, post.authorSlug);
    if (authorSlug) {
      schema.author["@id"] = `${SITE_URL}/authors/${authorSlug}#author`;
    }
  }

  // mentions[], GEO Phase 1: vendor/venue entity links from mentions_json column.
  // Each mention: { id, slug, name, type, categorySlug }
  const mentionsJson = defined(post.mentions_json, post.mentionsJson);
  if (Array.isArray(mentionsJson) && mentionsJson.length > 0) {
    schema.mentions = mentionsJson
      .filter(m => m?.slug)
      .map(m => {
        const ns = (m.categorySlug || m.category_slug || '') === 'wedding-venues' ? 'venues' : 'vendors';
        return {
          "@type": "LocalBusiness",
          "@id":   `${SITE_URL}/${ns}/${m.slug}#${ns === 'venues' ? 'venue' : 'vendor'}`,
          "name":  m.name || m.slug,
        };
      });
  }

  return schema;
}

// ── buildFaqPageSchema ────────────────────────────────────────────────────────
// FAQPage JSON-LD from the GEO faq_items JSONB column.
// Each item: { question: string, answer: string }
// AI engines (Perplexity, ChatGPT Search, Google AI Overviews) extract these
// verbatim for featured snippets and AI overview citations.
/**
 * @param {Array<{question: string, answer: string}>} faqItems
 */
export function buildFaqPageSchema(faqItems) {
  if (!Array.isArray(faqItems) || faqItems.length === 0) return null;

  const valid = faqItems.filter(item => item?.question?.trim() && item?.answer?.trim());
  if (valid.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type":    "FAQPage",
    "mainEntity": valid.map(item => ({
      "@type": "Question",
      "name":  item.question.trim(),
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  item.answer.trim(),
      },
    })),
  };
}

// ── buildBreadcrumbSchema ─────────────────────────────────────────────────────
// BreadcrumbList schema from a simple array of { name, url } objects.
/**
 * @param {Array<{name: string, url: string}>} crumbs
 */
export function buildBreadcrumbSchema(crumbs) {
  if (!crumbs || crumbs.length === 0) return null;

  // A crumb only survives if its URL is actually servable. If it is not, the
  // whole level is DROPPED, not kept as a name-only ListItem.
  //
  // Audited 2026-08-15: 145 of 314 public listings (46%) declared a region page in
  // their BreadcrumbList that returns 404, across 104 held regions. Leeds Castle, a
  // converted flagship client, told Google https://luxuryweddingdirectory.com/england/kent
  // was part of its hierarchy. Kent is deliberately unpublished.
  //
  // The first fix (2026-08-15) kept the level and stripped only the URL, reasoning
  // that Schema.org permits a ListItem with a name and no item, and that collapsing
  // the level would misstate the hierarchy. Schema.org does permit it. GOOGLE DOES
  // NOT: `item` is required on every ListItem except the last, and when one is
  // missing Google rejects the ENTIRE BreadcrumbList rather than that rung. So the
  // change made specifically to avoid misleading Google caused Google to receive
  // nothing at all from those 145 pages. Confirmed 2026-08-20 in Search Console
  // ("Missing field 'item'", 2 critical issues) and in the live HTML of Leeds Castle,
  // Villa Susanna, /bahamas/exuma/wedding-venues and /maldives/south-male-atoll/wedding-venues.
  //
  // Dropping is the lesser distortion. A shorter true trail still describes a real
  // path; an invalid list describes nothing, because it is discarded. Breadcrumbs
  // encode a navigable route, not an ontology, so a route that omits a rung with no
  // page is honest. Leeds Castle becomes Home > England > Wedding Venues > Leeds Castle.
  //
  // The LAST crumb is exempt: it is the page itself and conventionally carries no
  // URL, which is also the one case Google allows.
  const kept = crumbs.filter((crumb, i) => {
    if (i === crumbs.length - 1) return true;          // final crumb always survives
    if (!crumb || !crumb.url) return false;            // no URL and not last = invalid
    const pathOnly = crumb.url.startsWith("http")
      ? crumb.url.replace(SITE_URL, "")
      : crumb.url;
    return isPubliclyRoutable(pathOnly);
  });

  if (kept.length === 0) return null;

  return {
    "@context":        "https://schema.org",
    "@type":           "BreadcrumbList",
    // Positions renumber over the SURVIVING crumbs. Renumbering after the filter is
    // the point: a gap in position (1, 2, 4) is itself invalid, so filtering without
    // renumbering would trade one rejected list for another.
    "itemListElement": kept.map((crumb, i) => {
      const item = {
        "@type":    "ListItem",
        "position": i + 1,
        "name":     crumb.name,
      };
      const url = crumb.url && crumb.url.startsWith("http")
        ? crumb.url
        : (crumb.url ? `${SITE_URL}${crumb.url}` : null);
      const pathOnly = crumb.url && !crumb.url.startsWith("http")
        ? crumb.url
        : (crumb.url || "").replace(SITE_URL, "");
      // Unchanged from before: a URL is asserted only when it is servable. Every
      // non-final crumb already passed this test in the filter above, so in practice
      // this re-check only decides the FINAL crumb, whose behaviour is deliberately
      // left as it was. A name-only last ListItem is valid to Google.
      if (url && isPubliclyRoutable(pathOnly)) item.item = url;
      return item;
    }),
  };
}

// ── buildFaqSchema ────────────────────────────────────────────────────────────
// FAQPage schema from listing.faq_categories JSONB structure.
/**
 * @param {Array} faqCategories - listing.faq_categories / listing.faqCategories array from DB
 * Each category: { category: string, faqs: [{ question: string, answer: string }] }
 */
export function buildFaqSchema(faqCategories) {
  if (!faqCategories || !Array.isArray(faqCategories)) return null;

  const entities = [];
  for (const cat of faqCategories) {
    // The build writes categories as { questions: [{ q, a }] }; older/other
    // callers may use { faqs: [{ question, answer }] }. Accept both shapes so the
    // FAQPage (AEO) schema is emitted on the listing, not silently dropped.
    const qs = Array.isArray(cat.questions) ? cat.questions
             : Array.isArray(cat.faqs)      ? cat.faqs
             : [];
    for (const faq of qs) {
      const question = faq.question ?? faq.q;
      const answer   = faq.answer   ?? faq.a;
      if (question && answer) {
        entities.push({
          "@type":          "Question",
          "name":           question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text":  answer,
          },
        });
      }
    }
  }

  if (entities.length === 0) return null;

  return {
    "@context":   "https://schema.org",
    "@type":      "FAQPage",
    "mainEntity": entities,
  };
}

// ── autoGenerateListingFaqs ───────────────────────────────────────────────────
// Generates FAQ pairs from structured listing fields when faq_categories is
// empty or unpopulated. These fire FAQ rich snippets and AI Overview citations
// without any manual content effort.
//
// Accepts both snake_case (DB) and camelCase (mapped) field names.
// Returns: [{ question: string, answer: string }]  (flat, ready for ShowcaseFaq + buildFaqPageSchema)
// Returns empty array if insufficient data to generate meaningful answers.
//
export function autoGenerateListingFaqs(listing) {
  if (!listing) return [];
  const faqs = [];

  const name       = listing.name || listing.venue_name || listing.title || '';
  const city       = listing.city || '';
  const region     = listing.region || '';
  const country    = listing.country || '';
  const location   = [city, region, country].filter(Boolean).join(', ');

  // Capacity
  const cap = listing.ceremony_capacity || listing.capacityMax || listing.capacity_max || listing.capacity || listing.max_guests || '';
  if (name && cap) {
    faqs.push({
      question: `How many guests does ${name} accommodate?`,
      answer:   `${name} can accommodate up to ${cap} guests for a wedding ceremony${listing.dining_capacity ? `, with dining for up to ${listing.dining_capacity} guests` : ''}.`,
    });
  }

  // Accommodation
  if (name && listing.accommodation != null) {
    const hasAccomm = listing.accommodation === true || listing.accommodation === 'On-site' || listing.accommodation === 'yes';
    const bedrooms  = listing.bedrooms || listing.rooms_total || '';
    faqs.push({
      question: `Does ${name} have on-site accommodation?`,
      answer:   hasAccomm
        ? `Yes, ${name} offers on-site accommodation${bedrooms ? ` across ${bedrooms} bedrooms` : ''}, allowing wedding guests to stay on the property.`
        : `${name} does not have on-site accommodation. Your wedding team can advise on nearby hotels and transfers.`,
    });
  }

  // Outdoor ceremony
  if (name && listing.outdoor_ceremony != null) {
    const hasOutdoor = listing.outdoor_ceremony === true || listing.outdoor_ceremony === 'Available' || listing.outdoor_ceremony === 'yes';
    faqs.push({
      question: `Is an outdoor ceremony available at ${name}?`,
      answer:   hasOutdoor
        ? `Yes, ${name} offers outdoor ceremony options${location ? ` in ${location}` : ''}. Contact the venue to discuss ceremony locations and seasonal considerations.`
        : `${name} is licensed for indoor ceremonies. The venue team can advise on the available ceremony spaces.`,
    });
  }

  // Location
  if (name && location) {
    const nearestAirport = listing.nearest_airport || listing.airport || '';
    faqs.push({
      question: `Where is ${name} located?`,
      answer:   nearestAirport
        ? `${name} is located in ${location}, approximately ${nearestAirport} from the nearest major airport.`
        : `${name} is located in ${location}.`,
    });
  }

  // Exclusive use
  if (name && listing.exclusive_use != null) {
    const isExclusive = listing.exclusive_use === true || listing.exclusive_use === 'yes' || listing.exclusive_use === 'Yes';
    faqs.push({
      question: `Is ${name} available for exclusive use?`,
      answer:   isExclusive
        ? `Yes, ${name} is available on an exclusive use basis, meaning your wedding party will have the entire venue to yourselves.`
        : `${name} may operate other guests or events alongside your wedding. Contact the venue for availability and exclusivity options.`,
    });
  }

  // Pricing
  const priceFrom = listing.venue_hire_from || listing.typical_spend_min || listing.price_from || '';
  if (name && priceFrom) {
    faqs.push({
      question: `What is the starting price for a wedding at ${name}?`,
      answer:   `Venue hire at ${name} starts from ${priceFrom}. Final costs depend on guest numbers, chosen packages, and seasonal pricing. Contact the venue for a personalised quote.`,
    });
  }

  return faqs;
}

// ── buildWebSiteSchema ────────────────────────────────────────────────────────
// WebSite schema for the homepage - enables Sitelinks Search Box in Google.
export function buildWebSiteSchema() {
  return {
    "@context":        "https://schema.org",
    "@type":           "WebSite",
    "name":            SITE_NAME,
    "url":             SITE_URL,
    "potentialAction": {
      "@type":       "SearchAction",
      "target":      {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}
